import { useState } from 'react'
import {
  Button,
  Drawer,
  ProductPicker,
  type PickerValue,
} from '@/components/ui'

export interface ProductReassignDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Line context displayed at the top so user can confirm before picking. */
  parsedPartNumber: string | null
  parsedDescription: string | null
  /** Called with the selected SPI product id + display name when user
   *  hits "Reassign". The host page mutates via PATCH /lines/{id}. */
  onConfirm: (product: { id: number; name: string }) => void
}

/**
 * Sub-drawer for changing the matched product on a sales-order intake
 * line. Mirrors `ChangeProductDrawer` in the cofa-registry shell
 * service. Uses the existing ProductPicker primitive (server-search via
 * /api/pricelists/search/products).
 */
export function ProductReassignDrawer({
  open,
  onOpenChange,
  parsedPartNumber,
  parsedDescription,
  onConfirm,
}: ProductReassignDrawerProps) {
  const [picked, setPicked] = useState<PickerValue>(null)

  function handleConfirm() {
    if (picked) {
      onConfirm({ id: picked[0], name: picked[1] })
      setPicked(null)
      onOpenChange(false)
    }
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!o) setPicked(null)
        onOpenChange(o)
      }}
      size="standard"
      title="Reassign product"
      meta={
        parsedPartNumber || parsedDescription
          ? `Original: ${parsedPartNumber ?? ''}${parsedPartNumber && parsedDescription ? ' — ' : ''}${parsedDescription ?? ''}`
          : undefined
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={!picked}
          >
            Reassign
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm text-secondary">
          Search SPI's product catalog and pick the right product for this
          line. Saves the choice to the line and (on confirm) persists a
          customer-mapping so future intakes from this customer auto-match.
        </p>
        <ProductPicker
          value={picked}
          onChange={setPicked}
          placeholder="Type product name or SKU…"
        />
        {picked ? (
          <div className="rounded-xs border border-border-subtle bg-surface-sunk px-3 py-2 text-sm">
            <div className="font-medium text-primary">{picked[1]}</div>
            <div className="text-xs text-tertiary">id: {picked[0]}</div>
          </div>
        ) : null}
      </div>
    </Drawer>
  )
}
